// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import DateContentRow from "react-big-calendar/lib/DateContentRow";
import DateHeader from "react-big-calendar/lib/DateHeader";
import Header from "react-big-calendar/lib/Header";
import Popup from "react-big-calendar/lib/Popup";
import { navigate, views } from "react-big-calendar/lib/utils/constants";
import * as dates from "react-big-calendar/lib/utils/dates";
import { notify } from "react-big-calendar/lib/utils/helpers";
import Overlay from "react-overlays/Overlay";
import clsx from "clsx";
import * as animationFrame from "dom-helpers/animationFrame";
import getPosition from "dom-helpers/position";
import chunk from "lodash/chunk";
import { eventsForWeek, isToday, sortEvents } from "oc/utils";

export const MyLeaveCalendarTimelineView = (props) => {
  const {
    date,
    localizer,
    className,
    events: initialEvents,
    accessors,
    components,
    selectable,
    getNow,
    selected,
    longPressThreshold,
    getters,
    onSelectSlot,
    onSelectEvent,
    onDoubleClickEvent,
    onShowMore,
    onDrillDown,
    getDrilldownView,
    popup,
    popupOffset,
    handleDragStart,
    rtl,
  } = props;

  const [rowLimit, setRowLimit] = useState(5);
  const [needLimitMeasure, setNeedLimitMeasure] = useState(true);
  const [overlay, setOverlay] = useState(null);
  const slotRowRef = useRef();
  const containerRef = useRef();
  const selectTimerRef = useRef(null);
  const pendingSelectionRef = useRef([]);

  useEffect(() => {
    if (needLimitMeasure && slotRowRef.current) {
      setRowLimit(slotRowRef.current.getRowLimit());
      setNeedLimitMeasure(false);
    }
  }, [needLimitMeasure]);

  const handleResize = useCallback(() => {
    animationFrame.request(() => {
      setNeedLimitMeasure(true);
    });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    setNeedLimitMeasure(!dates.eq(date, props.date, "month"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  let month = dates.visibleDays(date, localizer);
  let weeks = chunk(month, month.length);
  let events = eventsForWeek(
    initialEvents,
    month[0],
    month[month.length - 1],
    accessors,
  );

  events.sort((a, b) => sortEvents(a, b, accessors));

  const handleSelectSlot = (range, slotInfo) => {
    pendingSelectionRef.current = [...pendingSelectionRef.current, ...range];
    clearTimeout(selectTimerRef.current);
    selectTimerRef.current = setTimeout(() => {
      const slots = [...pendingSelectionRef.current].sort((a, b) => +a - +b);
      pendingSelectionRef.current = [];
      notify(onSelectSlot, {
        slots,
        start: slots[0],
        end: slots[slots.length - 1],
        action: slotInfo.action,
        bounds: slotInfo.bounds,
        box: slotInfo.box,
      });
    });
  };

  const handleSelectEvent = (...args) => {
    pendingSelectionRef.current = [];
    notify(onSelectEvent, args);
  };

  const handleDoubleClickEvent = (...args) => {
    pendingSelectionRef.current = [];
    notify(onDoubleClickEvent, args);
  };

  const handleHeadingClick = (date, view, e) => {
    e.preventDefault();
    pendingSelectionRef.current = [];
    notify(onDrillDown, [date, view]);
  };

  const handleShowMore = (events, date, cell, slot, target) => {
    pendingSelectionRef.current = [];

    if (popup) {
      const position = getPosition(cell, containerRef.current);
      setOverlay({ date, events, position, target });
    } else {
      notify(onDrillDown, [date, getDrilldownView(date) || views.DAY]);
    }

    notify(onShowMore, [events, date, slot]);
  };

  const renderDateHeading = ({ date, className, key, ...rest }) => {
    const isOffRange = dates.month(date) !== dates.month(props.date);
    const isCurrent = dates.eq(date, props.date, "day");
    const drilldownView = getDrilldownView(date);
    const label = localizer.format(date, "dateFormat");
    const DateHeaderComponent = components.dateHeader || DateHeader;

    return (
      <div
        key={key}
        {...rest}
        className={clsx(className, {
          "rbc-off-range": isOffRange,
          "rbc-current": isCurrent,
        })}
      >
        <DateHeaderComponent
          label={label}
          date={date}
          drilldownView={drilldownView}
          isOffRange={isOffRange}
          onDrillDown={(e) => handleHeadingClick(date, drilldownView, e)}
        />
      </div>
    );
  };

  const renderHeaders = (row) => {
    const first = row[0];
    const last = row[row.length - 1];
    const HeaderComponent = components.header || Header;

    const defineClass = (day) => {
      if (isToday(day)) return "rbc-today";
      if (dates.month(date) !== dates.month(day)) return "rbc-off-range-bg";
      return null;
    };

    return dates.range(first, last, "day").map((day, idx) => {
      const isOffRange = dates.month(day) !== dates.month(date);
      return (
        <div
          key={`header_${idx}`}
          className={clsx("rbc-header", defineClass(day))}
        >
          <HeaderComponent
            date={day}
            localizer={localizer}
            label={localizer.format(day, "weekdayFormat")}
            isOffRange={isOffRange}
          />
        </div>
      );
    });
  };

  const renderMonth = (month, monthIdx) => {
    let monthEvents = eventsForWeek(
      initialEvents,
      month[0],
      month[month.length - 1],
      accessors,
    );
    monthEvents.sort((a, b) => sortEvents(a, b, accessors));

    return (
      <DateContentRow
        key={monthIdx}
        ref={monthIdx === 0 ? slotRowRef : undefined}
        container={() => containerRef.current}
        className="rbc-month-row"
        getNow={getNow}
        date={date}
        range={month}
        events={monthEvents}
        maxRows={rowLimit}
        selected={selected}
        selectable={selectable}
        components={components}
        accessors={accessors}
        getters={getters}
        localizer={localizer}
        renderHeader={renderDateHeading}
        renderForMeasure={needLimitMeasure}
        onShowMore={handleShowMore}
        onSelect={handleSelectEvent}
        onDoubleClick={handleDoubleClickEvent}
        onSelectSlot={handleSelectSlot}
        longPressThreshold={longPressThreshold}
        rtl={rtl}
      />
    );
  };

  const renderOverlay = () =>
    overlay && (
      <Overlay
        rootClose
        placement="bottom"
        show={!!overlay.position}
        onHide={() => setOverlay(null)}
        target={() => overlay.target}
      >
        {({ props }) => (
          <Popup
            {...props}
            popupOffset={popupOffset}
            accessors={accessors}
            getters={getters}
            selected={selected}
            components={components}
            localizer={localizer}
            position={overlay.position}
            show={() => setOverlay(null)}
            events={overlay.events}
            slotStart={overlay.date}
            slotEnd={overlay.end}
            onSelect={handleSelectEvent}
            onDoubleClick={handleDoubleClickEvent}
            handleDragStart={handleDragStart}
          />
        )}
      </Overlay>
    );

  return (
    <div className={clsx("rbc-month-view", className)}>
      <div className="rbc-row rbc-month-header">{renderHeaders(month)}</div>
      {weeks.map(renderMonth)}
      {popup && renderOverlay()}
    </div>
  );
};

MyLeaveCalendarTimelineView.range = (date, { localizer }) => {
  let start = dates.firstVisibleDay(date, localizer);
  let end = dates.lastVisibleDay(date, localizer);
  return { start, end };
};

MyLeaveCalendarTimelineView.navigate = (date, action) => {
  switch (action) {
    case navigate.PREVIOUS:
      return dates.add(date, -1, "month");
    case navigate.NEXT:
      return dates.add(date, 1, "month");
    default:
      return date;
  }
};

MyLeaveCalendarTimelineView.title = (date, { localizer }) =>
  localizer.format(date, "monthHeaderFormat");

export default MyLeaveCalendarTimelineView;
